/**
 * Custom hook for FAQs functionality
 * Provides a clean interface to interact with the FAQs store
 */
import { useEffect } from 'react';
import { useFaqsStore } from '../store/faqs.store';
import { FaqFilterOptions } from '../types/faqs.types';

/**
 * Hook that provides FAQs data and operations
 */
export const useFaqs = () => {
  const {
    faqs,
    categories,
    filteredFaqs,
    isLoading,
    error,
    filterOptions,
    fetchFaqs,
    createFaq,
    updateFaq,
    deleteFaq,
    setFilterOptions,
    resetFilters,
  } = useFaqsStore();

  // Fetch FAQs on component mount
  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  /**
   * Search FAQs by term
   * @param searchTerm Search term to filter FAQs
   */
  const searchFaqs = (searchTerm: string) => {
    setFilterOptions({ searchTerm });
  };

  /**
   * Filter FAQs by category
   * @param category Category to filter by, or null to clear category filter
   */
  const filterByCategory = (category: string | null) => {
    setFilterOptions({ selectedCategory: category });
  };

  /**
   * Update filter options
   * @param options New filter options
   */
  const updateFilters = (options: Partial<FaqFilterOptions>) => {
    setFilterOptions(options);
  };

  return {
    // Data
    faqs,
    filteredFaqs,
    categories,
    
    // State
    isLoading,
    error,
    filterOptions,
    
    // Actions
    searchFaqs,
    filterByCategory,
    updateFilters,
    resetFilters,
    createFaq,
    updateFaq,
    deleteFaq,
    refreshFaqs: fetchFaqs,
  };
};
